import { computed, inject, ref, watch, type Ref } from "vue";
import gql from "graphql-tag";

// Product details values that are also catalog filters link to the catalog
// with that one filter applied (/sunglasses?frame_shape=shield). The option
// keys are read from the catalog's own filter list, so a link only exists for
// a value the catalog can filter by and that still has products; anything
// else stays plain text instead of leading to an unfiltered listing.
//
// Frame and lens colours do not link: the catalog's Filters drawer shows only
// the colours curated by OpenCart's filter sort order (no lens colour at all),
// so a colour link would narrow the listing to a filter the shopper can
// neither see nor clear.

const FACET_BY_ROW: Record<string, string> = {
  "frame shape": "frame_shape",
  "frame material": "frame_material",
  "lens type": "lens_type",
  "made in": "made_in",
  polarized: "polarized",
  "rx-able": "rx_able",
  "rx able": "rx_able",
  "adjustable nosepads": "adjustable_nosepads",
};

// "Polarized: No" is not a collection anyone browses; Yes/No filters link
// only their "Yes".
const YES_ONLY = new Set(["polarized", "rx_able", "adjustable_nosepads"]);

// Catalog spellings that the filter module files under another option.
const VALUE_ALIASES: Record<string, Record<string, string>> = {
  frame_shape: { pilot: "pilot_aviator", geometric: "geometrical" },
};

// Sunglasses first: a product listed in both is a pair of sunglasses.
const CATALOG_PATHS = ["/sunglasses", "/eyeglasses"];

const CATALOG_PATH_QUERY = gql`
  query ProductDetailsCatalogPath($url: String) {
    searchUrl(url: $url) {
      type
      id
    }
  }
`;

const CATALOG_FACETS_QUERY = gql`
  query ProductDetailsCatalogFacets($input: CatalogSearchInput!) {
    catalogSearchV1(input: $input) {
      status
      facets {
        key
        values {
          key
          label
          count
        }
      }
    }
  }
`;

type FacetIndex = Map<
  string,
  { keys: Set<string>; byLetters: Map<string, string> }
>;

const decodeEntities = (text: string) =>
  text
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");

const optionKey = (text: string) =>
  decodeEntities(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const letters = (text: string) =>
  decodeEntities(text)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const rowFacet = (name: unknown) =>
  FACET_BY_ROW[
    String(name ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ")
  ];

const catalogPathOf = (product: Record<string, any> | null | undefined) => {
  const paths = (
    Array.isArray(product?.breadcrumbs) ? product.breadcrumbs : []
  ).map((crumb: any) => {
    try {
      return new URL(
        String(crumb?.url || ""),
        "https://hooliganseyewear.com"
      ).pathname
        .replace(/\/+$/, "")
        .toLowerCase();
    } catch {
      return "";
    }
  });
  return CATALOG_PATHS.find((path) => paths.includes(path)) || "";
};

// One request pair per catalog for the whole visit; a failure is forgotten so
// that the next opening of the panel tries again. The map is the only cache:
// Apollo's own would hand a DEGRADED answer back on that next opening.
const facetsByPath = new Map<string, Promise<FacetIndex | null>>();

const loadFacets = (apollo: any, path: string) => {
  const known = facetsByPath.get(path);
  if (known) return known;
  const request = (async () => {
    const route = await apollo.query({
      query: CATALOG_PATH_QUERY,
      variables: { url: path },
      fetchPolicy: "no-cache",
    });
    const target = route?.data?.searchUrl;
    if (target?.type !== "category" || !target?.id) return null;
    const result = await apollo.query({
      query: CATALOG_FACETS_QUERY,
      variables: {
        input: {
          categoryId: String(target.id),
          includeSubcategories: false,
          filters: [],
          page: 1,
          pageSize: 1,
        },
      },
      fetchPolicy: "no-cache",
    });
    const payload = result?.data?.catalogSearchV1;
    if (payload?.status !== "OK" || !Array.isArray(payload.facets)) return null;
    const index: FacetIndex = new Map();
    for (const facet of payload.facets) {
      const keys = new Set<string>();
      const byLetters = new Map<string, string>();
      for (const value of Array.isArray(facet?.values) ? facet.values : []) {
        const key = String(value?.key || "");
        if (!key || !(Number(value?.count) > 0)) continue;
        keys.add(key);
        byLetters.set(letters(key), key);
        byLetters.set(letters(String(value?.label || "")), key);
      }
      if (keys.size) index.set(String(facet?.key || ""), { keys, byLetters });
    }
    return index;
  })().catch(() => null);
  request.then((index) => {
    if (!index) facetsByPath.delete(path);
  });
  facetsByPath.set(path, request);
  return request;
};

export function useDetailFilterLinks(
  product: Ref<Record<string, any> | null | undefined>
) {
  const apollo = inject<any>("$vfapollo", null);
  const catalogPath = computed(() => catalogPathOf(product.value));
  const facets = ref<{ path: string; index: FacetIndex } | null>(null);
  let wanted = false;

  const resolve = () => {
    const path = catalogPath.value;
    if (import.meta.env.SSR || !wanted || !path) return;
    if (typeof apollo?.query !== "function") return;
    if (facets.value?.path === path) return;
    void loadFacets(apollo, path).then((index) => {
      if (index && catalogPath.value === path) facets.value = { path, index };
    });
  };

  // Called when Product details opens; nothing is requested before that.
  const load = () => {
    wanted = true;
    resolve();
  };

  watch(catalogPath, resolve);

  // The catalog location for one value of a row, or null for plain text.
  const linkFor = (rowName: unknown, value: string) => {
    const facet = rowFacet(rowName);
    const current = facets.value;
    if (!facet || !current || current.path !== catalogPath.value) return null;
    const options = current.index.get(facet);
    if (!options) return null;
    const key = optionKey(value);
    if (YES_ONLY.has(facet) && key !== "yes") return null;
    const alias = VALUE_ALIASES[facet]?.[key];
    const match = options.keys.has(alias || key)
      ? alias || key
      : options.byLetters.get(letters(value));
    return match ? { path: current.path, query: { [facet]: match } } : null;
  };

  return { load, linkFor };
}
